import {formatDashboardMoney} from './procurementDashboardCurrency.js';
import {poExpectedScope,verifyPoScope} from './poFinancialForm.js';

const own=(value,key)=>Object.prototype.hasOwnProperty.call(value||{},key);
export function poReviewDecimal(value) {
  if(typeof value==='number'&&(!Number.isFinite(value)||Math.abs(value)>Number.MAX_SAFE_INTEGER))return null;
  if(!['string','number'].includes(typeof value)||!/^\d{1,80}(?:\.\d{1,20})?$/.test(String(value)))return null;
  return String(value).replace(/^0+(?=\d)/,'').replace(/(\.\d*?)0+$/,'$1').replace(/\.$/,'');
}
function parts(value){const [whole,fraction='']=value.split('.');return {integer:BigInt(whole+fraction),scale:fraction.length};}
function text(integer,scale) {
  const digits=String(integer).padStart(scale+1,'0');
  return scale?digits.slice(0,-scale)+'.'+digits.slice(-scale):digits;
}
function product(a,b){if(a===null||b===null)return null;const x=parts(a),y=parts(b);return text(x.integer*y.integer,x.scale+y.scale);}
function add(values) {
  if(values.some(v=>v===null))return null;
  const parsed=values.map(parts),scale=Math.max(0,...parsed.map(v=>v.scale));
  return text(parsed.reduce((n,v)=>n+v.integer*10n**BigInt(scale-v.scale),0n),scale);
}
function cents(value) {
  if(value===null)return null;
  const {integer,scale}=parts(value);
  return scale<=2?integer*10n**BigInt(2-scale):(integer+5n*10n**BigInt(scale-3))/10n**BigInt(scale-2);
}
function currency(value){return typeof value==='string'&&/^[A-Z]{3}$/.test(value)?value:null;}
function recorded(header,name) {
  const exact=name+'_exact';
  // A missing exact value must not fall back to a rounded JavaScript projection.
  return own(header,exact)?(typeof header[exact]==='string'?poReviewDecimal(header[exact]):null):poReviewDecimal(header?.[name]);
}
export function verifyPoReviewSummary(value,user,poId) {
  verifyPoScope(value?.header?.response_scope,poExpectedScope(user));
  if(!/^[1-9]\d*$/.test(String(poId))||String(value.header.po_id)!==String(poId)||!Array.isArray(value.items)) {
    throw new Error('The requested purchase order could not be confirmed. Reopen it from the PO list.');
  }
  return value;
}
export function poReviewLine(item) {
  const quantity=poReviewDecimal(item.quantity??item.quantity_ordered),price=poReviewDecimal(item.unit_price);
  // tax is a stored tax amount. Only tax_rate is a percentage, including 0 and 0.5.
  const rate=poReviewDecimal(item.tax_rate);
  const net=product(quantity,price);
  const taxProduct=product(net,rate);
  const tax=taxProduct===null?null:text(parts(taxProduct).integer,parts(taxProduct).scale+2);
  return {quantity,price,rate,net,tax,gross:add([net,tax])};
}
export function poReviewFinancials(summary) {
  const header=summary?.header||{},items=Array.isArray(summary?.items)?summary.items:[];
  const lines=items.map(poReviewLine);
  const calculated={net:add(lines.map(line=>line.net)),tax:add(lines.map(line=>line.tax)),gross:add(lines.map(line=>line.gross))};
  const currencies={transaction:currency(header.transaction_currency),unit:currency(header.unit_currency),reporting:currency(header.reporting_currency)};
  const totals={},rates={unit:recorded(header,'fx_rate_to_unit'),reporting:recorded(header,'fx_rate_to_reporting')};
  let calculatedTotals=false,mismatch=false;
  for(const prefix of ['transaction','unit','reporting']) {
    totals[prefix]={};
    for(const part of ['net','tax','gross']) {
      const saved=recorded(header,prefix+'_'+part+'_total');
      totals[prefix][part]=saved;
      if(prefix==='transaction') {
        if(saved===null){totals.transaction[part]=calculated[part];calculatedTotals=true;}
        else if(calculated[part]!==null&&cents(saved)!==cents(calculated[part]))mismatch=true;
      }
    }
  }
  const reviewable=Boolean(currencies.transaction&&items.length&&lines.every(line=>line.quantity!==null&&line.price!==null&&line.rate!==null)&&
    Object.values(totals.transaction).every(value=>value!==null)&&!mismatch);
  return {currencies,totals,rates,lines,calculatedTotals,mismatch,reviewable};
}
export function formatPoReviewMoney(value,code,locale='en-GB') {
  if(poReviewDecimal(value)===null||!currency(code))return 'Unavailable';
  try{return formatDashboardMoney(String(value),code,locale);}
  catch{return 'Unavailable';}
}
export function poReviewDate(value) {
  if(typeof value!=='string')return 'Not recorded';
  const match=/^(\d{4}-\d{2}-\d{2})/.exec(value);
  if(!match)return 'Not recorded';
  const parsed=new Date(match[1]+'T00:00:00.000Z');
  return Number.isFinite(parsed.getTime())&&parsed.toISOString().slice(0,10)===match[1]?match[1]:'Not recorded';
}
export function formatPoReviewDay(value) {
  const day=poReviewDate(value);
  if(day==='Not recorded')return '—';
  const [year,month,date]=day.split('-');
  return date+'/'+month+'/'+year;
}
